import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useRunScoring } from '../hooks/useScoring.js';
import { ScoringForm } from '../components/scoring/ScoringForm';
import { ClassificationResult } from '../components/scoring/ClassificationResult';
import { ScoreGrade } from '../components/scoring/ScoreGrade';
import { AxisChart } from '../components/scoring/AxisChart';
import { RentalPlanTable } from '../components/scoring/RentalPlanTable';
import { RiskAssessment } from '../components/scoring/RiskAssessment';
import { MarketIntelligence } from '../components/scoring/MarketIntelligence';
import { TrendSignalCard } from '../components/scoring/TrendSignalCard';
import { SnsBuzzCard } from '../components/scoring/SnsBuzzCard';
import { CompetitorCard } from '../components/scoring/CompetitorCard';
import { Card } from '../components/ui/Card';
import type { ScoringResultWithId } from '../types/api.js';

/**
 * @graph-node {Page}
 * @graph-stack mall
 * @graph-domain Operations
 * @graph-business メインスコアリングページ。商品名/URL入力→AI判定→スコアリング→結果表示（判定結果・グレード・5軸チャート・レンタルプラン・リスク・マーケット情報・トレンド・SNS・競合）
 * @graph-connects useRunScoring [calls] スコアリング実行フック呼び出し
 * @graph-connects ScoringForm [calls] スコアリングフォームコンポーネント呼び出し
 * @graph-connects ClassificationResult [calls] AI判定結果コンポーネント呼び出し
 * @graph-connects ScoreGrade [calls] グレードバッジコンポーネント呼び出し
 * @graph-connects AxisChart [calls] 5軸チャートコンポーネント呼び出し
 * @graph-connects RentalPlanTable [calls] レンタルプランテーブルコンポーネント呼び出し
 * @graph-connects RiskAssessment [calls] リスクアセスメントコンポーネント呼び出し
 * @graph-connects MarketIntelligence [calls] マーケットインテリジェンスコンポーネント呼び出し
 * @graph-connects TrendSignalCard [calls] トレンドシグナルコンポーネント呼び出し
 * @graph-connects SnsBuzzCard [calls] SNSバズ度コンポーネント呼び出し
 * @graph-connects CompetitorCard [calls] 競合・二次流通・口コミコンポーネント呼び出し
 */
export const Route = createFileRoute('/')({
  component: ScoringPage,
});

/** ローディングステップ
 * @graph-connects none
 */
const LOADING_STEPS = [
  'scoring.loadingSteps.resolving',
  'scoring.loadingSteps.classifying',
  'scoring.loadingSteps.scraping',
  'scoring.loadingSteps.scoring',
] as const;

/** @graph-connects none */
function ScoringPage() {
  const { t } = useTranslation();
  const scoringMutation = useRunScoring();
  const [result, setResult] = useState<ScoringResultWithId | null>(null);
  const [loadingStep, setLoadingStep] = useState(0);

  const handleSubmit = (input: string) => {
    setResult(null);
    setLoadingStep(0);

    // ローディングステップを段階的に進める
    const stepInterval = setInterval(() => {
      setLoadingStep((prev) => {
        if (prev >= LOADING_STEPS.length - 1) {
          clearInterval(stepInterval);
          return prev;
        }
        return prev + 1;
      });
    }, 3000);

    scoringMutation.mutate(
      { input },
      {
        onSuccess: (data) => {
          clearInterval(stepInterval);
          setResult(data);
        },
        onError: () => {
          clearInterval(stepInterval);
        },
      },
    );
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* ページヘッダー */}
      <div>
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
          {t('scoring.title')}
        </h1>
        <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
          {t('scoring.description')}
        </p>
      </div>

      {/* スコアリングフォーム */}
      <ScoringForm onSubmit={handleSubmit} isLoading={scoringMutation.isPending} />

      {/* ローディング中のスケルトン表示 */}
      {scoringMutation.isPending && (
        <div className="space-y-4 animate-fade-in">
          {/* ステップ進捗バー */}
          <Card variant="glass">
            <div className="flex items-center gap-4">
              <div className="loading-spinner loading-spinner-sm" />
              <div className="flex-1">
                <p className="text-sm font-medium text-[var(--color-text-primary)]">
                  {t(LOADING_STEPS[loadingStep])}
                </p>
                <div className="flex gap-1 mt-2">
                  {LOADING_STEPS.map((_, i) => (
                    <div
                      key={i}
                      className={`h-1 flex-1 rounded-full transition-colors duration-300 ${
                        i <= loadingStep
                          ? 'bg-[var(--color-accent-primary)]'
                          : 'bg-[var(--color-bg-elevated)]'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </Card>

          {/* スケルトン: グレード + 強み・弱み */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card variant="glass" className="flex flex-col items-center gap-4 py-8">
              <div className="w-20 h-20 rounded-full skeleton-shimmer" />
              <div className="w-24 h-8 rounded skeleton-shimmer" />
            </Card>
            <Card variant="glass">
              <div className="space-y-3">
                <div className="w-16 h-4 rounded skeleton-shimmer" />
                <div className="w-full h-3 rounded skeleton-shimmer" />
                <div className="w-3/4 h-3 rounded skeleton-shimmer" />
                <div className="w-16 h-4 rounded skeleton-shimmer mt-4" />
                <div className="w-full h-3 rounded skeleton-shimmer" />
                <div className="w-2/3 h-3 rounded skeleton-shimmer" />
              </div>
            </Card>
          </div>

          {/* スケルトン: 5軸チャート */}
          <Card variant="glass">
            <div className="w-24 h-4 rounded skeleton-shimmer mb-4" />
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="w-32 h-3 rounded skeleton-shimmer" />
                    <div className="w-8 h-3 rounded skeleton-shimmer" />
                  </div>
                  <div className="score-bar">
                    <div
                      className="h-full rounded-full skeleton-shimmer"
                      style={{ width: `${String(60 + i * 5)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* スケルトン: レンタルプランテーブル */}
          <Card variant="glass">
            <div className="w-40 h-4 rounded skeleton-shimmer mb-3" />
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="flex items-center gap-4 py-2 border-t border-[var(--color-border)]"
                >
                  <div className="w-24 h-3 rounded skeleton-shimmer" />
                  <div className="w-16 h-3 rounded skeleton-shimmer ml-auto" />
                  <div className="w-16 h-3 rounded skeleton-shimmer" />
                  <div className="w-12 h-3 rounded skeleton-shimmer" />
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* エラー表示 */}
      {scoringMutation.isError && (
        <Card variant="glass" className="animate-fade-in">
          <div className="risk-warning">
            <p className="text-sm text-[var(--color-text-primary)]">
              {scoringMutation.error?.message ?? t('scoring.error')}
            </p>
          </div>
        </Card>
      )}

      {/* 結果表示 */}
      {result && (
        <div className="space-y-4 animate-fade-in-up">
          {/* AI判定結果 */}
          {result.classification && (
            <ClassificationResult
              classification={result.classification}
              brandLabel={result.brandLabel}
              categoryKey={result.categoryKey}
            />
          )}

          {/* グレードと総合スコア */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ScoreGrade
              grade={result.grade}
              total={result.total}
              gradeColor={result.gradeColor}
              gradeBg={result.gradeBg}
            />

            {/* 強み・弱み */}
            <Card variant="glass">
              <div className="space-y-4">
                {result.strengths.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-[var(--color-success)] mb-2">
                      {t('scoring.strengths')}
                    </h4>
                    <ul className="space-y-1">
                      {result.strengths.map((s, i) => (
                        <li
                          key={i}
                          className="text-sm text-[var(--color-text-secondary)] flex items-start gap-2"
                        >
                          <span className="text-[var(--color-success)] mt-0.5">+</span>
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {result.weaknesses.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-[var(--color-error)] mb-2">
                      {t('scoring.weaknesses')}
                    </h4>
                    <ul className="space-y-1">
                      {result.weaknesses.map((w, i) => (
                        <li
                          key={i}
                          className="text-sm text-[var(--color-text-secondary)] flex items-start gap-2"
                        >
                          <span className="text-[var(--color-error)] mt-0.5">-</span>
                          {w}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {result.advice && (
                  <div className="pt-2 border-t border-[var(--color-border)]">
                    <p className="text-sm text-[var(--color-text-secondary)] italic">
                      {result.advice}
                    </p>
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* 5軸チャート */}
          <AxisChart axes={result.axes} />

          {/* レンタルプラン */}
          <RentalPlanTable plans={result.rentalPlans} />

          {/* リスクアセスメント */}
          <RiskAssessment risks={result.risks} />

          {/* マーケットインテリジェンス */}
          <MarketIntelligence result={result} />

          {/* ── 市場データセクション（スクレイピング結果） ── */}
          {result.marketData && (
            <>
              {/* トレンドシグナル + SNSバズ度 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TrendSignalCard trendSignal={result.marketData.trendSignal} />
                <SnsBuzzCard snsBuzz={result.marketData.snsBuzz} />
              </div>

              {/* 競合レンタル・二次流通・口コミ */}
              <CompetitorCard
                competitorRentals={result.marketData.competitorRentals}
                secondaryMarket={result.marketData.secondaryMarket}
                concerns={result.marketData.concerns}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}
